import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createWriting, getWritingById, updateWriting } from '../../api/writingApi';
import {
  TextField,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
} from '@mui/material';
import Editor from '../../components/common/Editor';
import { useToast } from '../../contexts/ToastContext';

const defaultQuestion = (num) => ({ label: '', questionNumber: num, image: '' });

const WritingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const editorRefs = [useRef(), useRef()];

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    label: '',
    questions: [defaultQuestion(1), defaultQuestion(2)],
  });
  const [errors, setErrors] = useState({ label: '', questions: [{}, {}] });

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        try {
          const response = await getWritingById(id);
          const data = response.data || response;
          const qs = data.questions || data.Questions || [];
          
          setFormData({
            label: data.label || '',
            questions: [qs[0] || defaultQuestion(1), qs[1] || defaultQuestion(2)],
          });
          
          // Set editor content after a small delay to ensure refs are ready
          setTimeout(() => {
            qs.forEach((q, idx) => {
              if (q && editorRefs[idx].current) {
                editorRefs[idx].current.setHTML(q.label || '');
              }
            });
          }, 100);
        } catch (err) {
          console.error('Failed to fetch writing:', err);
        }
      };
      fetchData();
    }
  }, [id]);

  const saveCurrentEditor = (index) => {
    const html = editorRefs[index].current?.getHTML() || '';
    setFormData((prev) => {
      const updated = [...prev.questions];
      updated[index].label = html;
      return { ...prev, questions: updated };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleQuestionField = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.questions];
      updated[index][field] = value;
      return { ...prev, questions: updated };
    });
    setErrors((prev) => {
      const updated = [...prev.questions];
      if (!prev.questions[index]) updated[index] = {};
      else updated[index] = { ...prev.questions[index], [field]: '' };
      return { ...prev, questions: updated };
    });
  };

  const validateStep = (index) => {
    const errs = {};
    const q = formData.questions[index];
    const html = editorRefs[index].current?.getHTML() || q.label || '';
    if (!html || html === '<p><br></p>') errs.label = 'Question text is required';
    if (!q.questionNumber) errs.questionNumber = 'Question number is required';
    setErrors((prev) => {
      const updated = [...prev.questions];
      updated[index] = errs;
      return { ...prev, questions: updated };
    });
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    saveCurrentEditor(activeStep);
    if (validateStep(activeStep)) {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    saveCurrentEditor(activeStep);
    setActiveStep((s) => s - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    saveCurrentEditor(activeStep);
    const allValid = [0, 1].every((idx) => validateStep(idx));
    if (!formData.label.trim()) {
      setErrors((prev) => ({ ...prev, label: 'Label is required' }));
      return;
    }
    if (!allValid) return;

    try {
      if (id) {
        await updateWriting(id, formData);
        showToast('Writing updated successfully!', 'success');
      } else {
        await createWriting(formData);
        showToast('Writing created successfully!', 'success');
      }
      navigate('/writings');
    } catch (err) {
      console.error('Failed to save writing:', err);
      showToast('Failed to save writing', 'error');
    }
  };

  const questionKeys = ['Task 1', 'Task 2'];

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 4 }}>
      <Typography variant="h4" gutterBottom>
        {id ? 'Edit Writing' : 'Create Writing'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Label"
          name="label"
          value={formData.label}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={Boolean(errors.label)}
          helperText={errors.label}
        />
        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {questionKeys.map((q, idx) => (
            <Step key={q}>
              <StepLabel>{q}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {(() => {
          const idx = activeStep;
          const q = formData.questions[idx];
          return (
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    {questionKeys[idx]} Content
                  </Typography>
                  <Editor 
                    ref={editorRefs[idx]} 
                    defaultValue={q.label || ''} 
                    key={`editor-${idx}-${q.label || 'empty'}`}
                  />
                  {errors.questions[idx]?.label && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {errors.questions[idx].label}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Question Number"
                    type="number"
                    value={q.questionNumber}
                    onChange={(e) => handleQuestionField(idx, 'questionNumber', e.target.value)}
                    fullWidth
                    margin="normal"
                    error={Boolean(errors.questions[idx]?.questionNumber)}
                    helperText={errors.questions[idx]?.questionNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Image URL"
                    value={q.image}
                    onChange={(e) => handleQuestionField(idx, 'image', e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Box>
          );
        })()}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
            Back
          </Button>
          {activeStep < 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button type="submit" variant="contained">
              Save
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
};

export default WritingForm;
